import {ICurriculumsService} from './ICurriculumsService';
import {CurriculumsRepository} from './CurriculumsRepository';
import {LevelsRepository} from '../Level/LevelsRepository';
import {PlansRepository} from '../Plans/PlansRepository';
import {fetchUserAttributes} from 'aws-amplify/auth';
import {NewCreateCurriculumInput} from '@PikaDoApp/npm-pikado-logic/dist/modules/curriculum/graphql/curriculum.interface';
import {CurriculumTypeEnum} from '@PikaDoApp/npm-pikado-logic';
import {CurriculumUI} from './CurriculumTypes';

/**
 * CORRECTED TYPE DEFINITION for UpdateCurriculumInput
 *
 * Note: The package type definition (@PikaDoApp/npm-pikado-logic) incorrectly defines
 * placementTest as a single string, but the API actually expects an array of strings.
 * This local interface provides the correct type signature.
 */
interface CurriculumUpdateInput {
  id: string;
  name?: string;
  type?: CurriculumTypeEnum;
  program?: string;
  createdBy?: string;
  level?: string[];
  grades?: string[];
  placementTest?: string[]; // ✅ CORRECT: Array of placement test IDs
  isPublished?: boolean;
  isAcademic?: boolean;
}

export class CurriculumsService implements ICurriculumsService {
  curriculumsRepository: CurriculumsRepository;
  levelsRepository: LevelsRepository;
  planRepository: PlansRepository;

  constructor() {
    this.curriculumsRepository = new CurriculumsRepository();
    this.levelsRepository = new LevelsRepository();
    this.planRepository = new PlansRepository();
  }

  async getCurriculums(): Promise<CurriculumUI[]> {
    let curriculums = await this.curriculumsRepository.getCurriculums();
    return curriculums;
  }

  async getCurriculumById(id: string): Promise<CurriculumUI | null> {
    let curriculum = await this.curriculumsRepository.getCurriculumById(id);
    return curriculum;
  }

  /**
   * Creates a new curriculum using the @PikaDoApp/npm-pikado-logic package
   *
   * @param curriculum - The curriculum object containing the name
   * @param selectedProgramId - The program ID to associate with the curriculum
   * @param selectedLevelIds - Array of level IDs to associate with the curriculum
   * @param curriculumType - The type of curriculum
   * @param selectedPlacementTestIds - Optional array of placement test IDs
   * @returns The created curriculum object
   */
  async createNewCurriculum(
    curriculum: CurriculumUI,
    selectedProgramId: string,
    selectedLevelIds: string[],
    curriculumType: CurriculumTypeEnum,
    selectedPlacementTestIds?: string[],
    selectedGradeIds?: string[],
    isPublished?: boolean,
    isAcademic?: boolean,
  ): Promise<CurriculumUI> {
    try {
      // Get current user for createdBy field
      const currentUser = await fetchUserAttributes();
      const userId = currentUser.sub || 'unknown';

      // Prepare curriculum data for new logic package
      const curriculumToCreate: NewCreateCurriculumInput = {
        name: curriculum.name,
        type: curriculumType,
        level: selectedLevelIds,
        grades: selectedGradeIds,
        program: selectedProgramId,
        createdBy: userId,
        placementTest: selectedPlacementTestIds,
        isPublished: isPublished,
        isAcademic: isAcademic,
      };

      // Create curriculum using new logic package
      const createdCurriculum =
        await this.curriculumsRepository.createNewCurriculum(
          curriculumToCreate,
        );

      // If placement tests are selected, update the curriculum to add them
      // (The create API doesn't support placement tests in some versions, so we update after creation if needed)
      // Note: Current npm-pikado-logic seems to include them in create, but we keep the logic for safety
      if (selectedPlacementTestIds && selectedPlacementTestIds.length > 0) {
        const minimalCurriculum: any = {
          name: curriculum.name,
        };

        const updateResult = await this.updateNewCurriculum(
          createdCurriculum.id,
          minimalCurriculum,
          undefined,
          undefined,
          selectedPlacementTestIds,
        );
        return updateResult;
      }

      return createdCurriculum;
    } catch (error) {
      console.error(
        '🔍 [CURRICULUM-CREATE] ❌ ERROR during curriculum creation:',
        error,
      );
      throw error;
    }
  }

  /**
   * Updates an existing curriculum using the @PikaDoApp/npm-pikado-logic package
   */
  async updateNewCurriculum(
    id: string,
    curriculum: CurriculumUI,
    selectedProgramId?: string,
    selectedLevelIds?: string[],
    selectedPlacementTestIds?: string[],
    curriculumType?: CurriculumTypeEnum,
    selectedGradeIds?: string[],
    isPublished?: boolean,
    isAcademic?: boolean,
  ): Promise<CurriculumUI> {
    try {
      const updateInput: CurriculumUpdateInput = {
        id: id,
      };

      if (curriculum.name) {
        updateInput.name = curriculum.name;
      }
      if (curriculumType) {
        updateInput.type = curriculumType;
      }
      if (selectedProgramId) {
        updateInput.program = selectedProgramId;
      }
      if (selectedLevelIds && selectedLevelIds.length > 0) {
        updateInput.level = selectedLevelIds;
      }
      if (selectedPlacementTestIds !== undefined) {
        updateInput.placementTest = selectedPlacementTestIds;
      }
      if (selectedGradeIds !== undefined) {
        updateInput.grades = selectedGradeIds;
      }
      if (isPublished !== undefined) {
        updateInput.isPublished = isPublished;
      }
      if (isAcademic !== undefined) {
        updateInput.isAcademic = isAcademic;
      }

      const updatedCurriculum =
        await this.curriculumsRepository.updateNewCurriculum(updateInput);

      return updatedCurriculum;
    } catch (error) {
      console.error(
        '🔍 [CURRICULUM-NEW-PACKAGE] updateNewCurriculum service Error:',
        error,
      );
      throw error;
    }
  }

  async softDeleteNewCurriculum(id: string): Promise<boolean> {
    try {
      const result =
        await this.curriculumsRepository.softDeleteNewCurriculum(id);
      return result;
    } catch (error) {
      console.error(
        '🔍 [CURRICULUM-NEW-PACKAGE] softDeleteNewCurriculum service Error:',
        error,
      );
      throw error;
    }
  }
}
